<script setup>
import { useToastStore } from '@/stores/toastStore'
import IconCheck from '@/design/icons/IconCheck.vue'
import IconClose from '@/design/icons/IconClose.vue'

const toast = useToastStore()
</script>

<template>
  <Transition name="toast-drop">
    <div
      v-if="toast.message"
      class="toast"
      :class="`toast--${toast.message.tone}`"
      role="status"
      aria-live="polite"
    >
      <span class="toast__icon" aria-hidden="true">
        <IconCheck v-if="toast.message.tone === 'success'" class="toast__svg"/>
        <IconClose v-else-if="toast.message.tone === 'error'" class="toast__svg"/>
        <svg v-else class="toast__svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="square">
          <circle cx="12" cy="12" r="9"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
        </svg>
      </span>
      <span class="toast__text">{{ toast.message.text }}</span>
      <button class="toast__close" aria-label="Dismiss" @click="toast.dismiss">
        <IconClose class="toast__close-icon"/>
      </button>
    </div>
  </Transition>
</template>

<style scoped>
.toast {
  position: fixed;
  top: calc(var(--header-h) + var(--sp-4));
  left: 50%;
  transform: translateX(-50%);
  z-index: 200;
  display: inline-flex;
  align-items: center;
  gap: var(--sp-3);
  padding: var(--sp-3) var(--sp-4) var(--sp-3) var(--sp-3);
  border-radius: var(--r-sm);
  background: rgba(11, 10, 8, 0.95);
  border: 1px solid var(--gold);
  box-shadow: var(--shadow-3);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  max-width: calc(100% - var(--sp-8));
  font-size: var(--fs-14);
  color: var(--ink);
}
.toast--success { border-color: var(--gold); }
.toast--error   { border-color: var(--ember); }

.toast__icon {
  display: inline-flex;
  width: 26px;
  height: 26px;
  border-radius: var(--r-xs);
  align-items: center;
  justify-content: center;
  background: rgba(229, 199, 107, 0.12);
  color: var(--gold);
  flex-shrink: 0;
}
.toast--error .toast__icon { background: rgba(230, 76, 43, 0.12); color: var(--ember); }
.toast__svg { width: 16px; height: 16px; }

.toast__text { flex: 1; line-height: 1.4; }

.toast__close {
  width: 26px;
  height: 26px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--ink-soft);
  border-radius: var(--r-xs);
  flex-shrink: 0;
  transition: color var(--t-fast) var(--ease-road), background var(--t-fast) var(--ease-road);
}
.toast__close:hover { color: var(--gold); background: rgba(229, 199, 107, 0.08); }
.toast__close-icon { width: 14px; height: 14px; }

.toast-drop-enter-active, .toast-drop-leave-active {
  transition: transform var(--t-base) var(--ease-cinema), opacity var(--t-base) var(--ease-cinema);
}
.toast-drop-enter-from, .toast-drop-leave-to {
  transform: translate(-50%, -16px);
  opacity: 0;
}

@media (max-width: 560px) {
  .toast {
    top: calc(var(--header-h) + var(--sp-2));
    left: var(--sp-3);
    right: var(--sp-3);
    transform: none;
    max-width: none;
  }
  .toast-drop-enter-from, .toast-drop-leave-to {
    transform: translateY(-16px);
  }
}
</style>
